import React, { useState, useEffect, useMemo } from "react";
import GenerateQuote from "../../services/generatequote.service";
import Swal from "sweetalert2";
import { ExportToExcel } from "../../components/ExportToExcel";
import quoteBlueBird from "bluebird";
import { useNavigate } from "react-router-dom";

const CURRENCY_SYMBOL = "€";
const API_DELAY_MS = 2000;
const OUT_OF_STOCK = "out of Stock";
const DEFAULT_PASTE_MESSAGE = "Paste Your Excel Sheet Here.";
const PART_NUMBER_REGEX = /^[A-Za-z0-9\-._,/+:;#&()\\ ]+$/;
const CURRENCY_REGEX = /[₹,€]/g;
const ROUNDING_FACTOR = 100;
const ALPHANUMERIC_REGEX = /[^a-zA-Z0-9]/g;
const PRICE_CLEANUP_REGEX = /[^\d.,]/g;

const SUPPLIERS = {
  ARROW: "Arrow",
  FARNELL: "Farnell",
  TME: "TME",
  MOUSER: "Mouser",
  DIGIKEY: "DigiKey",
  LCSC: "Lcsc",
};

const ERROR_MESSAGES = {
  SESSION_EXPIRED: "Session Expire,Try Login again.",
  PASTE_DATA_REQUIRED: "Please paste proper data.",
  SUPPLIER_REQUIRED: "Please Select one of the supplier.",
  PCBS_QUANTITY_REQUIRED: "Please Enter Proper PCBs Quantity.",
  UNITS_PER_PCB_NUMBER_ONLY: "unitsPerPCB allow only number.",
  UNITS_PER_PCB_REQUIRED: "unitsPerPCB Value require.",
  PART_NUMBER_INVALID: "Part Number is not valid.",
};

const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch (e) {
    return null;
  }
};

const extractPrice = (priceString) => {
  if (!priceString || priceString === 0) return 0;
  return parseFloat(String(priceString).replace(CURRENCY_REGEX, "")) || 0;
};

const roundToTwoDecimals = (value) => {
  return Math.round(value * ROUNDING_FACTOR) / ROUNDING_FACTOR;
};

const updateLowestPrice = (price, availability, currentLowest) => {
  if (
    price > 0 &&
    availability !== 0 &&
    availability !== OUT_OF_STOCK &&
    (price < currentLowest || currentLowest === 0)
  ) {
    return price;
  }
  return currentLowest;
};

const isValidPrice = (price, availability, lowestPrice) => {
  return (
    price === lowestPrice &&
    price !== 0 &&
    availability !== 0 &&
    availability !== OUT_OF_STOCK
  );
};

const handleQuantityChange = (e, setter) => {
  setter((v) =>
    e.target.validity.valid ? e.target.value.replace(/\D/g, "") : v
  );
};

const showError = (message) => {
  Swal.fire({
    title: "Error!",
    text: message,
    icon: "error",
  });
};

const handleQuoteError = (err, setLoading) => {
  setLoading(false);
  showError(err?.message || "An error occurred while generating the quote.");
};

const showSessionError = (navigate) => {
  Swal.fire({
    icon: "error",
    title: "Error",
    text: ERROR_MESSAGES.SESSION_EXPIRED,
  }).then(() => {
    navigate("/login", { replace: true });
  });
};

const updateSupplierTotals = (
  setFirst,
  setSecond,
  setThird,
  firstValue,
  secondValue,
  thirdValue
) => {
  setFirst((prev) => roundToTwoDecimals(prev + firstValue));
  setSecond((prev) => roundToTwoDecimals(prev + secondValue));
  setThird((prev) => roundToTwoDecimals(prev + thirdValue));
};

const addToSupplierData = (setter, item) => {
  setter((prev) => [...prev, item]);
};

const addToMultipleSupplierData = (setters, item) => {
  setters.forEach((setter) => addToSupplierData(setter, item));
};

const hasSupplierInData = (data, supplier) => {
  return data.some((el) => el["Supplier"] === supplier);
};

const updateSupplierLowestPrices = (
  priceFirst,
  priceSecond,
  priceThird,
  item,
  lowestFirst,
  lowestSecond,
  lowestThird
) => {
  return {
    first: updateLowestPrice(priceFirst, item.AvailabilityFirst, lowestFirst),
    second: updateLowestPrice(
      priceSecond,
      item.AvailabilitySecond,
      lowestSecond
    ),
    third: updateLowestPrice(priceThird, item.AvailabilityThird, lowestThird),
  };
};

const updateLowestItem = (
  newLowest,
  currentLowest,
  price,
  item,
  availability,
  currentLowestItem
) => {
  if (newLowest !== currentLowest) {
    return { lowest: newLowest, item };
  }
  if (
    newLowest === currentLowest &&
    currentLowest > 0 &&
    price === currentLowest &&
    availability !== 0 &&
    availability !== OUT_OF_STOCK &&
    !currentLowestItem
  ) {
    return { lowest: currentLowest, item };
  }
  return { lowest: currentLowest, item: currentLowestItem };
};

const createDefaultSupplierEntry = (
  partName,
  totalQtyFirst,
  totalQtySecond,
  totalQtyThird,
  supplier
) => ({
  "Part Name": partName,
  "Total Quantity 1": totalQtyFirst,
  "Total Quantity 2": totalQtySecond,
  "Total Quantity 3": totalQtyThird,
  Footprint: "",
  manufacturerName: "",
  Supplier: supplier,
  AvailabilityFirst: 0,
  AvailabilitySecond: 0,
  AvailabilityThird: 0,
  "Unit Price 1": 0,
  "Unit Price 2": 0,
  "Unit Price 3": 0,
  MOQ: 0,
  "Subtotal Price 1": 0,
  "Subtotal Price 2": 0,
  "Subtotal Price 3": 0,
});

function UserDashboard() {
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const userStr = localStorage.getItem("user");
      if (!userStr) {
        showSessionError(navigate);
        return;
      }
      const user = JSON.parse(userStr);
      if (!user) {
        showSessionError(navigate);
        return;
      }
      const decodedJwt = parseJwt(user.accessToken);
      if (!decodedJwt || decodedJwt.exp * 1000 < Date.now()) {
        showSessionError(navigate);
      }
    } catch (error) {
      showSessionError(navigate);
    }
  }, [navigate]);
  const [pastedData, setPastedData] = useState([]);
  const [pastedMessage, setPastedMessage] = useState(DEFAULT_PASTE_MESSAGE);
  const [pastedErrorMessage, setPastedErrorMessage] = useState("");
  const [generateQuoteData, setGenerateQuoteData] = useState([]);
  const [arrow, setArrow] = useState(false);
  const [tme, setTme] = useState(false);
  const [farnell, setFarnell] = useState(false);
  const [digikey, setDigikey] = useState(false);
  const [mouser, setMouser] = useState(false);
  const [lcsc, setLcsc] = useState(false);
  const [arrowTotalFirst, setArrowTotalFirst] = useState(0.0);
  const [arrowTotalSecond, setArrowTotalSecond] = useState(0.0);
  const [arrowTotalThird, setArrowTotalThird] = useState(0.0);
  const [tmeTotalFirst, setTmeTotalFirst] = useState(0.0);
  const [tmeTotalSecond, setTmeTotalSecond] = useState(0.0);
  const [tmeTotalThird, setTmeTotalThird] = useState(0.0);
  const [farnellTotalFirst, setFarnellTotalFirst] = useState(0.0);
  const [farnellTotalSecond, setFarnellTotalSecond] = useState(0.0);
  const [farnellTotalThird, setFarnellTotalThird] = useState(0.0);
  const [mouserTotalFirst, setMouserTotalFirst] = useState(0.0);
  const [mouserTotalSecond, setMouserTotalSecond] = useState(0.0);
  const [mouserTotalThird, setMouserTotalThird] = useState(0.0);
  const [digikeyTotalFirst, setDigikeyTotalFirst] = useState(0.0);
  const [digikeyTotalSecond, setDigikeyTotalSecond] = useState(0.0);
  const [digikeyTotalThird, setDigikeyTotalThird] = useState(0.0);
  const [lcscTotalFirst, setLcscTotalFirst] = useState(0.0);
  const [lcscTotalSecond, setLcscTotalSecond] = useState(0.0);
  const [lcscTotalThird, setLcscTotalThird] = useState(0.0);
  const [arrowData, setArrowData] = useState([]);
  const [tmeData, setTmeData] = useState([]);
  const [farnellData, setFarnellData] = useState([]);
  const [digikeyData, setDigikeyData] = useState([]);
  const [mouserData, setMouserData] = useState([]);
  const [lcscData, setLcscData] = useState([]);
  const [pcbquantityFirst, setPcbquantityFirst] = useState(1);
  const [pcbquantitySecond, setPcbquantitySecond] = useState(1);
  const [pcbquantityThird, setPcbquantityThird] = useState(1);
  const [generateExcelData, setGenerateExcelData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [style, setStyle] = useState("");

  const fileName = useMemo(() => {
    const today = new Date();
    const pad = (num) => String(num).padStart(2, "0");
    const date = `${pad(today.getHours())}-${pad(today.getMinutes())}-${pad(
      today.getSeconds()
    )}-${pad(today.getDate())}-${pad(
      today.getMonth() + 1
    )}-${today.getFullYear()}`;
    return `GenerateQuoteData-${date}`;
  }, []);

  const changeStyle = () => {
    setStyle("border-width");
  };

  const excelTotalData = useMemo(() => {
    const suppliers = [
      {
        name: "Arrow Total",
        data: arrowData,
        totals: [arrowTotalFirst, arrowTotalSecond, arrowTotalThird],
      },
      {
        name: "TME Total",
        data: tmeData,
        totals: [tmeTotalFirst, tmeTotalSecond, tmeTotalThird],
      },
      {
        name: "Farnell Total",
        data: farnellData,
        totals: [farnellTotalFirst, farnellTotalSecond, farnellTotalThird],
      },
      {
        name: "Mouser Total",
        data: mouserData,
        totals: [mouserTotalFirst, mouserTotalSecond, mouserTotalThird],
      },
      {
        name: "DigiKey Total",
        data: digikeyData,
        totals: [digikeyTotalFirst, digikeyTotalSecond, digikeyTotalThird],
      },
      {
        name: "LCSC Total",
        data: lcscData,
        totals: [lcscTotalFirst, lcscTotalSecond, lcscTotalThird],
      },
    ];

    return suppliers
      .filter((supplier) => supplier.data.length > 0)
      .map((supplier) => ({
        SupplierName: supplier.name,
        "PCBs Quantity 1": CURRENCY_SYMBOL + supplier.totals[0],
        "PCBs Quantity 2": CURRENCY_SYMBOL + supplier.totals[1],
        "PCBs Quantity 3": CURRENCY_SYMBOL + supplier.totals[2],
      }));
  }, [
    farnellData,
    arrowData,
    digikeyData,
    mouserData,
    tmeData,
    lcscData,
    arrowTotalFirst,
    arrowTotalSecond,
    arrowTotalThird,
    digikeyTotalFirst,
    digikeyTotalSecond,
    digikeyTotalThird,
    farnellTotalFirst,
    farnellTotalSecond,
    farnellTotalThird,
    mouserTotalFirst,
    mouserTotalSecond,
    mouserTotalThird,
    tmeTotalFirst,
    tmeTotalSecond,
    tmeTotalThird,
    lcscTotalFirst,
    lcscTotalSecond,
    lcscTotalThird,
  ]);

  useEffect(() => {
    const handlePaste = (event) => {
      if (
        event.target.tagName &&
        event.target.tagName.match(/(input|textarea)/i)
      ) {
        return;
      }
      setPastedErrorMessage("");

      const clipboardData = event.clipboardData || window.clipboardData;
      const pastedText = clipboardData.getData("text");
      const rows = pastedText.split("\n");
      const result = rows
        .map((row) => {
          const cells = row.split("\t");
          const partName = cells[0]?.trim();
          const unitsPerPCB = cells[1]?.trim();

          if (!partName) {
            return null;
          }

          if (!unitsPerPCB) {
            setPastedErrorMessage(ERROR_MESSAGES.UNITS_PER_PCB_REQUIRED);
            return {
              partName,
              unitsPerPCB: "",
              color: "errorMessage",
            };
          }

          if (isNaN(unitsPerPCB)) {
            setPastedErrorMessage(ERROR_MESSAGES.UNITS_PER_PCB_NUMBER_ONLY);
          }

          if (!PART_NUMBER_REGEX.test(partName)) {
            setPastedErrorMessage(ERROR_MESSAGES.PART_NUMBER_INVALID);
            return {
              partName,
              unitsPerPCB,
              color: "errorMessage",
            };
          }

          return {
            partName,
            unitsPerPCB,
            color: "",
          };
        })
        .filter((el) => el !== null);
      if (result.length > 0) {
        setPastedMessage("");
      } else {
        setPastedMessage(DEFAULT_PASTE_MESSAGE);
      }
      setPastedData(result);
    };
    document.addEventListener("paste", handlePaste);
    return () => {
      document.removeEventListener("paste", handlePaste);
    };
  }, []);
  const handleLcsc = () => {
    navigate("/user/lcsc", { replace: true });
  };
  const handleReset = () => {
    setGenerateQuoteData([]);
    setPastedMessage(DEFAULT_PASTE_MESSAGE);
    setPastedData([]);
    setArrow(false);
    setTme(false);
    setFarnell(false);
    setDigikey(false);
    setMouser(false);
    setLcsc(false);
    setArrowTotalFirst(0.0);
    setArrowTotalSecond(0.0);
    setArrowTotalThird(0.0);
    setTmeTotalFirst(0.0);
    setTmeTotalSecond(0.0);
    setTmeTotalThird(0.0);
    setFarnellTotalFirst(0.0);
    setFarnellTotalSecond(0.0);
    setFarnellTotalThird(0.0);
    setMouserTotalFirst(0.0);
    setMouserTotalSecond(0.0);
    setMouserTotalThird(0.0);
    setDigikeyTotalFirst(0.0);
    setDigikeyTotalSecond(0.0);
    setDigikeyTotalThird(0.0);
    setLcscTotalFirst(0.0);
    setLcscTotalSecond(0.0);
    setLcscTotalThird(0.0);
    setPcbquantityFirst(1);
    setPcbquantitySecond(1);
    setPcbquantityThird(1);
    setGenerateExcelData([]);
    setArrowData([]);
    setTmeData([]);
    setFarnellData([]);
    setMouserData([]);
    setDigikeyData([]);
    setLcscData([]);
    setStyle("");
    setPastedErrorMessage("");
  };
  const handleGenerateQuote = () => {
    if (pastedData.length === 0) {
      showError(ERROR_MESSAGES.PASTE_DATA_REQUIRED);
      return null;
    }
    if (pastedErrorMessage !== "") {
      showError(pastedErrorMessage);
      return null;
    }

    if (!mouser && !arrow && !tme && !farnell && !digikey && !lcsc) {
      showError(ERROR_MESSAGES.SUPPLIER_REQUIRED);
      return null;
    }
    if (pcbquantityFirst < 1 || pcbquantitySecond < 1 || pcbquantityThird < 1) {
      showError(ERROR_MESSAGES.PCBS_QUANTITY_REQUIRED);
      return null;
    }
    try {
      setGenerateQuoteData([]);
      setArrowData([]);
      setTmeData([]);
      setFarnellData([]);
      setMouserData([]);
      setDigikeyData([]);
      setLcscData([]);
      setArrowTotalFirst(0.0);
      setArrowTotalSecond(0.0);
      setArrowTotalThird(0.0);
      setTmeTotalFirst(0.0);
      setTmeTotalSecond(0.0);
      setTmeTotalThird(0.0);
      setFarnellTotalFirst(0.0);
      setFarnellTotalSecond(0.0);
      setFarnellTotalThird(0.0);
      setMouserTotalFirst(0.0);
      setMouserTotalSecond(0.0);
      setMouserTotalThird(0.0);
      setDigikeyTotalFirst(0.0);
      setDigikeyTotalSecond(0.0);
      setDigikeyTotalThird(0.0);
      setLcscTotalFirst(0.0);
      setLcscTotalSecond(0.0);
      setLcscTotalThird(0.0);
      setGenerateExcelData([]);
      setLoading(true);
      setLoadingMessage("generating Quote.");
      quoteBlueBird
        .map(
          pastedData,
          async (items) => {
            await quoteBlueBird.delay(API_DELAY_MS);
            let gqData = {
              [items.partName]: [],
            };
            let totalQtyFirst = Math.round(
              items.unitsPerPCB * pcbquantityFirst
            );
            let totalQtySecond = Math.round(
              items.unitsPerPCB * pcbquantitySecond
            );
            let totalQtyThird = Math.round(
              items.unitsPerPCB * pcbquantityThird
            );
            if (arrow) {
              let footPrint = "";
              let manufacturerName = "";
              let availabilityFirst = OUT_OF_STOCK;
              let availabilitySecond = OUT_OF_STOCK;
              let availabilityThird = OUT_OF_STOCK;
              let moq = 0;
              let getArrowResponse = await GenerateQuote.getArrow(
                items.partName
              );
              if (
                "itemserviceresult" in getArrowResponse &&
                getArrowResponse.itemserviceresult.data.length > 0 &&
                getArrowResponse.itemserviceresult.transactionArea &&
                getArrowResponse.itemserviceresult.transactionArea.length > 0 &&
                getArrowResponse.itemserviceresult.transactionArea[0].response
                  .success === true
              ) {
                getArrowResponse.itemserviceresult.data[0].PartList.forEach(
                  (arrowItems) => {
                    if (
                      items.partName === arrowItems.partNum &&
                      "InvOrg" in arrowItems
                    ) {
                      arrowItems.InvOrg.webSites.forEach((webItems) => {
                        webItems.sources.forEach((sourceItems) => {
                          if (sourceItems.currency === "EUR") {
                            sourceItems.sourceParts.forEach(
                              (sourcePartsItems) => {
                                const normalizedProductCode =
                                  sourcePartsItems.productCode.replace(
                                    ALPHANUMERIC_REGEX,
                                    ""
                                  );
                                const normalizedPartName =
                                  items.partName.replace(
                                    ALPHANUMERIC_REGEX,
                                    ""
                                  );
                                if (
                                  normalizedProductCode === normalizedPartName
                                ) {
                                  footPrint = "";
                                  manufacturerName =
                                    arrowItems.manufacturer.mfrName;
                                  moq =
                                    sourcePartsItems.minimumOrderQuantity || 1;

                                  const packSize =
                                    sourcePartsItems.packSize || 1;
                                  const priceTiers =
                                    sourcePartsItems.Prices.resaleList;

                                  const computePrice = (totalQty) => {
                                    const usedQty = Math.max(totalQty, moq);
                                    const adjustedQty =
                                      Math.ceil(usedQty / packSize) * packSize;

                                    let foundTier = priceTiers.find(
                                      (tier) =>
                                        adjustedQty >= tier.minQty &&
                                        adjustedQty <= tier.maxQty
                                    );

                                    if (!foundTier) {
                                      foundTier =
                                        priceTiers[priceTiers.length - 1];
                                    }

                                    const price = foundTier?.price || 0;
                                    const subTotal = adjustedQty * price;

                                    return {
                                      price,
                                      subTotal,
                                      usedQty: adjustedQty,
                                    };
                                  };

                                  const {
                                    price: priceFirst,
                                    subTotal: subTotalPriceFirst,
                                    usedQty: usedQtyFirst,
                                  } = computePrice(totalQtyFirst);
                                  const {
                                    price: priceSecond,
                                    subTotal: subTotalPriceSecond,
                                    usedQty: usedQtySecond,
                                  } = computePrice(totalQtySecond);
                                  const {
                                    price: priceThird,
                                    subTotal: subTotalPriceThird,
                                    usedQty: usedQtyThird,
                                  } = computePrice(totalQtyThird);

                                  const availabilityQty =
                                    sourcePartsItems.Availability &&
                                    sourcePartsItems.Availability.length > 0
                                      ? sourcePartsItems.Availability[0].fohQty
                                      : 0;

                                  if (availabilityQty > totalQtyFirst)
                                    availabilityFirst = availabilityQty;
                                  if (availabilityQty > totalQtySecond)
                                    availabilitySecond = availabilityQty;
                                  if (availabilityQty > totalQtyThird)
                                    availabilityThird = availabilityQty;

                                  gqData[items.partName].push({
                                    "Part Name": items.partName,
                                    "Total Quantity 1": usedQtyFirst,
                                    "Total Quantity 2": usedQtySecond,
                                    "Total Quantity 3": usedQtyThird,
                                    manufacturerName: manufacturerName,
                                    Footprint: footPrint,
                                    Supplier: SUPPLIERS.ARROW,
                                    AvailabilityFirst: availabilityFirst,
                                    AvailabilitySecond: availabilitySecond,
                                    AvailabilityThird: availabilityThird,
                                    "Unit Price 1":
                                      CURRENCY_SYMBOL + priceFirst,
                                    "Unit Price 2":
                                      CURRENCY_SYMBOL + priceSecond,
                                    "Unit Price 3":
                                      CURRENCY_SYMBOL + priceThird,
                                    MOQ: moq,
                                    "Subtotal Price 1":
                                      CURRENCY_SYMBOL +
                                      roundToTwoDecimals(subTotalPriceFirst),
                                    "Subtotal Price 2":
                                      CURRENCY_SYMBOL +
                                      roundToTwoDecimals(subTotalPriceSecond),
                                    "Subtotal Price 3":
                                      CURRENCY_SYMBOL +
                                      roundToTwoDecimals(subTotalPriceThird),
                                  });
                                }
                              }
                            );
                          }
                        });
                      });
                    }
                  }
                );
              }
              if (!hasSupplierInData(gqData[items.partName], SUPPLIERS.ARROW)) {
                gqData[items.partName].push(
                  createDefaultSupplierEntry(
                    items.partName,
                    totalQtyFirst,
                    totalQtySecond,
                    totalQtyThird,
                    SUPPLIERS.ARROW
                  )
                );
              }
            }

            if (mouser) {
              const getMouserPrice = (priceBreaks, totalQty, moq = 1) => {
                if (!Array.isArray(priceBreaks) || priceBreaks.length === 0) {
                  return { price: 0, subTotal: 0, usedQty: 0 };
                }

                const tiers = priceBreaks
                  .map((pb) => ({
                    qty: pb.Quantity,
                    price: parseFloat(
                      pb.Price.replace(PRICE_CLEANUP_REGEX, "").replace(
                        ",",
                        "."
                      )
                    ),
                  }))
                  .sort((a, b) => a.qty - b.qty);

                let usedQty = totalQty < moq ? moq : totalQty;

                let selected = tiers[0];
                for (const t of tiers) {
                  if (usedQty >= t.qty) selected = t;
                }

                return {
                  price: selected.price,
                  subTotal: selected.price * usedQty,
                  usedQty,
                };
              };

              let footPrint = "";
              let manufacturerName = "";
              let availabilityFirst = OUT_OF_STOCK;
              let availabilitySecond = OUT_OF_STOCK;
              let availabilityThird = OUT_OF_STOCK;
              let moq = 0;
              let getMouserResponse = await GenerateQuote.getMouser(
                items.partName
              );
              if (
                "SearchResults" in getMouserResponse &&
                getMouserResponse.SearchResults !== null
              ) {
                // eslint-disable-next-line
                getMouserResponse.SearchResults.Parts.forEach((item) => {
                  if (items.partName === item.ManufacturerPartNumber) {
                    if ("Manufacturer" in item) {
                      manufacturerName = item.Manufacturer;
                    }
                    if (totalQtyFirst < item.AvailabilityInStock) {
                      availabilityFirst = item.AvailabilityInStock;
                    }
                    if (totalQtySecond < item.AvailabilityInStock) {
                      availabilitySecond = item.AvailabilityInStock;
                    }
                    if (totalQtyThird < item.AvailabilityInStock) {
                      availabilityThird = item.AvailabilityInStock;
                    }

                    moq = item.Min || 1;

                    const adjustQty = (qty) => {
                      let adjusted = Math.max(qty, moq);
                      if (
                        item.Mult &&
                        item.Mult > 0 &&
                        adjusted % item.Mult !== 0
                      ) {
                        adjusted = Math.ceil(adjusted / item.Mult) * item.Mult;
                      }
                      return adjusted;
                    };

                    const qty1 = adjustQty(totalQtyFirst);
                    const qty2 = adjustQty(totalQtySecond);
                    const qty3 = adjustQty(totalQtyThird);

                    const { price: priceFirst, subTotal: subTotalPriceFirst } =
                      getMouserPrice(item.PriceBreaks, qty1, moq);
                    const {
                      price: priceSecond,
                      subTotal: subTotalPriceSecond,
                    } = getMouserPrice(item.PriceBreaks, qty2, moq);
                    const { price: priceThird, subTotal: subTotalPriceThird } =
                      getMouserPrice(item.PriceBreaks, qty3, moq);

                    gqData[items.partName].push({
                      "Part Name": items.partName,
                      "Total Quantity 1": qty1,
                      "Total Quantity 2": qty2,
                      "Total Quantity 3": qty3,
                      manufacturerName: manufacturerName,
                      Footprint: footPrint,
                      Supplier: SUPPLIERS.MOUSER,
                      AvailabilityFirst: availabilityFirst,
                      AvailabilitySecond: availabilitySecond,
                      AvailabilityThird: availabilityThird,
                      "Unit Price 1": CURRENCY_SYMBOL + priceFirst,
                      "Unit Price 2": CURRENCY_SYMBOL + priceSecond,
                      "Unit Price 3": CURRENCY_SYMBOL + priceThird,
                      MOQ: moq,
                      "Subtotal Price 1":
                        CURRENCY_SYMBOL +
                        roundToTwoDecimals(subTotalPriceFirst),
                      "Subtotal Price 2":
                        CURRENCY_SYMBOL +
                        roundToTwoDecimals(subTotalPriceSecond),
                      "Subtotal Price 3":
                        CURRENCY_SYMBOL +
                        roundToTwoDecimals(subTotalPriceThird),
                    });
                  }
                });
              }
              if (
                !hasSupplierInData(gqData[items.partName], SUPPLIERS.MOUSER)
              ) {
                gqData[items.partName].push(
                  createDefaultSupplierEntry(
                    items.partName,
                    totalQtyFirst,
                    totalQtySecond,
                    totalQtyThird,
                    SUPPLIERS.MOUSER
                  )
                );
              }
            }
            if (farnell) {
              const getFarnellPrice = (prices, totalQty, moq = 1) => {
                if (!Array.isArray(prices) || prices.length === 0) {
                  return { price: 0, subTotal: 0, usedQty: 0 };
                }

                const qty = Math.max(totalQty, moq);

                const tier =
                  prices.find((p) => qty >= p.from && qty <= p.to) || prices[0];

                return {
                  price: tier.cost,
                  subTotal: tier.cost * qty,
                  usedQty: qty,
                };
              };

              let priceFirst = 0;
              let priceSecond = 0;
              let priceThird = 0;
              let footPrint = "";
              let manufacturerName = "";
              let availabilityFirst = OUT_OF_STOCK;
              let availabilitySecond = OUT_OF_STOCK;
              let availabilityThird = OUT_OF_STOCK;
              let subTotalPriceFirst = 0;
              let subTotalPriceSecond = 0;
              let subTotalPriceThird = 0;
              let getFarnellResponse = await GenerateQuote.getFarnell(
                items.partName
              );
              if (
                "manufacturerPartNumberSearchReturn" in getFarnellResponse &&
                getFarnellResponse.manufacturerPartNumberSearchReturn !==
                  null &&
                getFarnellResponse.manufacturerPartNumberSearchReturn
                  .numberOfResults > 0
              ) {
                getFarnellResponse.manufacturerPartNumberSearchReturn.products.forEach(
                  (item) => {
                    if (
                      items.partName === item.translatedManufacturerPartNumber
                    ) {
                      manufacturerName = item.vendorName || "";
                      let fp = "";
                      let pine = "";
                      if (item.attributes) {
                        item.attributes.forEach((attb) => {
                          if (
                            attb.attributeLabel.includes("Encapsulado") &&
                            fp === ""
                          ) {
                            fp = attb.attributeValue;
                          }
                          if (attb.attributeUnit === "Pines") {
                            pine = attb.attributeValue;
                          }
                        });
                      }
                      footPrint = pine === "" ? fp : `${fp} - ${pine}`;

                      const stock = item.stock?.level || 0;
                      availabilityFirst =
                        stock > totalQtyFirst ? stock : OUT_OF_STOCK;
                      availabilitySecond =
                        stock > totalQtySecond ? stock : OUT_OF_STOCK;
                      availabilityThird =
                        stock > totalQtyThird ? stock : OUT_OF_STOCK;

                      const moq = item.translatedMinimumOrderQuality || 1;
                      const orderMultiple =
                        item.orderMultiples > 0 ? item.orderMultiples : 1;

                      const adjustQty = (qty) => {
                        let adjusted = Math.max(qty, moq);
                        if (
                          orderMultiple > 1 &&
                          adjusted % orderMultiple !== 0
                        ) {
                          adjusted =
                            Math.ceil(adjusted / orderMultiple) * orderMultiple;
                        }
                        return adjusted;
                      };

                      const qty1 = adjustQty(totalQtyFirst);
                      const qty2 = adjustQty(totalQtySecond);
                      const qty3 = adjustQty(totalQtyThird);

                      if (item.prices?.length) {
                        const r1 = getFarnellPrice(item.prices, qty1, moq);
                        const r2 = getFarnellPrice(item.prices, qty2, moq);
                        const r3 = getFarnellPrice(item.prices, qty3, moq);

                        priceFirst = r1.price;
                        subTotalPriceFirst = r1.subTotal;

                        priceSecond = r2.price;
                        subTotalPriceSecond = r2.subTotal;

                        priceThird = r3.price;
                        subTotalPriceThird = r3.subTotal;
                      }

                      gqData[items.partName].push({
                        "Part Name": items.partName,
                        "Total Quantity 1": qty1,
                        "Total Quantity 2": qty2,
                        "Total Quantity 3": qty3,
                        Footprint: footPrint,
                        manufacturerName: manufacturerName,
                        Supplier: SUPPLIERS.FARNELL,
                        AvailabilityFirst: availabilityFirst,
                        AvailabilitySecond: availabilitySecond,
                        AvailabilityThird: availabilityThird,
                        "Unit Price 1": CURRENCY_SYMBOL + priceFirst,
                        "Unit Price 2": CURRENCY_SYMBOL + priceSecond,
                        "Unit Price 3": CURRENCY_SYMBOL + priceThird,
                        MOQ: moq,
                        "Subtotal Price 1":
                          CURRENCY_SYMBOL +
                          roundToTwoDecimals(subTotalPriceFirst),
                        "Subtotal Price 2":
                          CURRENCY_SYMBOL +
                          roundToTwoDecimals(subTotalPriceSecond),
                        "Subtotal Price 3":
                          CURRENCY_SYMBOL +
                          roundToTwoDecimals(subTotalPriceThird),
                      });
                    }
                  }
                );
              }
              if (
                !hasSupplierInData(gqData[items.partName], SUPPLIERS.FARNELL)
              ) {
                gqData[items.partName].push(
                  createDefaultSupplierEntry(
                    items.partName,
                    totalQtyFirst,
                    totalQtySecond,
                    totalQtyThird,
                    SUPPLIERS.FARNELL
                  )
                );
              }
            }
            if (tme) {
              const getTmePrice = (priceList, totalQty, moq = 1) => {
                if (!Array.isArray(priceList) || priceList.length === 0) {
                  return { price: 0, subTotal: 0, usedQty: 0 };
                }

                const qty = Math.max(totalQty, moq);

                let selected = priceList[0];
                for (const priceItem of priceList) {
                  if (qty >= priceItem.Amount) {
                    selected = priceItem;
                  } else {
                    break;
                  }
                }

                return {
                  price: selected.PriceValue,
                  subTotal: selected.PriceValue * qty,
                  usedQty: qty,
                };
              };

              let footPrint = "";
              let manufacturerName = "";
              let availabilityFirst = OUT_OF_STOCK;
              let availabilitySecond = OUT_OF_STOCK;
              let availabilityThird = OUT_OF_STOCK;
              let moq = 0;
              let getTmeResponse = await GenerateQuote.getTme(items.partName);

              if (!("Error" in getTmeResponse)) {
                if (getTmeResponse.Symbol === items.partName) {
                  manufacturerName = getTmeResponse.Producer || "";
                  const stock = getTmeResponse.Amount || 0;
                  availabilityFirst =
                    stock > totalQtyFirst ? stock : OUT_OF_STOCK;
                  availabilitySecond =
                    stock > totalQtySecond ? stock : OUT_OF_STOCK;
                  availabilityThird =
                    stock > totalQtyThird ? stock : OUT_OF_STOCK;
                  moq = getTmeResponse.MinAmount || 1;

                  const multiples =
                    getTmeResponse.Multiples > 0 ? getTmeResponse.Multiples : 1;

                  const adjustQty = (qty) => {
                    let adjusted = Math.max(qty, moq);
                    if (multiples > 1 && adjusted % multiples !== 0) {
                      adjusted = Math.ceil(adjusted / multiples) * multiples;
                    }
                    return adjusted;
                  };

                  const qty1 = adjustQty(totalQtyFirst);
                  const qty2 = adjustQty(totalQtySecond);
                  const qty3 = adjustQty(totalQtyThird);

                  const r1 = getTmePrice(getTmeResponse.PriceList, qty1, moq);
                  const r2 = getTmePrice(getTmeResponse.PriceList, qty2, moq);
                  const r3 = getTmePrice(getTmeResponse.PriceList, qty3, moq);
                  let fp = "",
                    pine = "",
                    casing = "";
                  if (Array.isArray(getTmeResponse.ParameterList)) {
                    getTmeResponse.ParameterList.forEach((param) => {
                      if (param.ParameterName === "Case - inch")
                        fp = param.ParameterValue;
                      if (param.ParameterName === "Case - mm")
                        pine = param.ParameterValue;
                      if (param.ParameterName === "Case")
                        casing = param.ParameterValue;
                    });
                  }

                  footPrint =
                    pine === ""
                      ? `${casing} ${fp}`
                      : `${casing} ${fp} (${pine} metric)`;
                  gqData[items.partName].push({
                    "Part Name": items.partName,
                    "Total Quantity 1": qty1,
                    "Total Quantity 2": qty2,
                    "Total Quantity 3": qty3,
                    Footprint: footPrint,
                    manufacturerName: manufacturerName,
                    Supplier: SUPPLIERS.TME,
                    AvailabilityFirst: availabilityFirst,
                    AvailabilitySecond: availabilitySecond,
                    AvailabilityThird: availabilityThird,
                    "Unit Price 1": CURRENCY_SYMBOL + r1.price,
                    "Unit Price 2": CURRENCY_SYMBOL + r2.price,
                    "Unit Price 3": CURRENCY_SYMBOL + r3.price,
                    MOQ: moq,
                    "Subtotal Price 1":
                      CURRENCY_SYMBOL + roundToTwoDecimals(r1.subTotal),
                    "Subtotal Price 2":
                      CURRENCY_SYMBOL + roundToTwoDecimals(r2.subTotal),
                    "Subtotal Price 3":
                      CURRENCY_SYMBOL + roundToTwoDecimals(r3.subTotal),
                  });
                }
              }
              if (!hasSupplierInData(gqData[items.partName], SUPPLIERS.TME)) {
                gqData[items.partName].push(
                  createDefaultSupplierEntry(
                    items.partName,
                    totalQtyFirst,
                    totalQtySecond,
                    totalQtyThird,
                    SUPPLIERS.TME
                  )
                );
              }
            }
            if (digikey) {
              const getDigiKeyPrice = (
                priceTiers,
                totalQty,
                moq = 1,
                fallbackPrice = 0
              ) => {
                if (!Array.isArray(priceTiers) || priceTiers.length === 0) {
                  return {
                    price: fallbackPrice,
                    subTotal: fallbackPrice * totalQty,
                    usedQty: totalQty,
                  };
                }

                const usedQty = totalQty < moq ? moq : totalQty;

                const tiers = [...priceTiers].sort(
                  (a, b) => a.BreakQuantity - b.BreakQuantity
                );

                let selectedTier = tiers[0];
                for (const t of tiers) {
                  if (usedQty >= t.BreakQuantity) selectedTier = t;
                }

                const price = selectedTier.UnitPrice || fallbackPrice;
                const subTotal = price * usedQty;

                return { price, subTotal, usedQty };
              };

              let footPrint = "";
              let manufacturerName = "";
              let availabilityFirst = OUT_OF_STOCK;
              let availabilitySecond = OUT_OF_STOCK;
              let availabilityThird = OUT_OF_STOCK;
              let moq = 0;
              let getdigikeyResponse = await GenerateQuote.getDigikey(
                items.partName
              );
              if (getdigikeyResponse.success) {
                let dgData = getdigikeyResponse.data.ProductDetails;
                dgData.forEach((item) => {
                  if (item.ManufacturerPartNumber === items.partName) {
                    manufacturerName = item.Supplier || "";
                    footPrint = "";
                    moq = item.MinimumOrderQuantity || 1;

                    availabilityFirst =
                      item.QuantityAvailable > totalQtyFirst
                        ? item.QuantityAvailable
                        : OUT_OF_STOCK;
                    availabilitySecond =
                      item.QuantityAvailable > totalQtySecond
                        ? item.QuantityAvailable
                        : OUT_OF_STOCK;
                    availabilityThird =
                      item.QuantityAvailable > totalQtyThird
                        ? item.QuantityAvailable
                        : OUT_OF_STOCK;

                    const result1 = getDigiKeyPrice(
                      item.StandardPricing,
                      totalQtyFirst,
                      moq,
                      item.UnitPrice
                    );
                    const result2 = getDigiKeyPrice(
                      item.StandardPricing,
                      totalQtySecond,
                      moq,
                      item.UnitPrice
                    );
                    const result3 = getDigiKeyPrice(
                      item.StandardPricing,
                      totalQtyThird,
                      moq,
                      item.UnitPrice
                    );

                    if (Array.isArray(item.Parameters)) {
                      const pkg = item.Parameters.find(
                        (p) => p.Parameter === "Package / Case"
                      );
                      if (pkg) footPrint = pkg.Value;
                    }

                    gqData[items.partName].push({
                      "Part Name": items.partName,
                      "Total Quantity 1": result1.usedQty,
                      "Total Quantity 2": result2.usedQty,
                      "Total Quantity 3": result3.usedQty,
                      manufacturerName: manufacturerName,
                      Footprint: footPrint,
                      Supplier: SUPPLIERS.DIGIKEY,
                      AvailabilityFirst: availabilityFirst,
                      AvailabilitySecond: availabilitySecond,
                      AvailabilityThird: availabilityThird,
                      "Unit Price 1": CURRENCY_SYMBOL + result1.price,
                      "Unit Price 2": CURRENCY_SYMBOL + result2.price,
                      "Unit Price 3": CURRENCY_SYMBOL + result3.price,
                      MOQ: moq,
                      "Subtotal Price 1":
                        CURRENCY_SYMBOL + roundToTwoDecimals(result1.subTotal),
                      "Subtotal Price 2":
                        CURRENCY_SYMBOL + roundToTwoDecimals(result2.subTotal),
                      "Subtotal Price 3":
                        CURRENCY_SYMBOL + roundToTwoDecimals(result3.subTotal),
                    });
                  }
                });
              }
              if (
                !hasSupplierInData(gqData[items.partName], SUPPLIERS.DIGIKEY)
              ) {
                gqData[items.partName].push(
                  createDefaultSupplierEntry(
                    items.partName,
                    totalQtyFirst,
                    totalQtySecond,
                    totalQtyThird,
                    SUPPLIERS.DIGIKEY
                  )
                );
              }
            }

            if (lcsc) {
              const getLcscPrice = (
                priceTiers,
                totalQty,
                moq = 1,
                orderMultiple = 1
              ) => {
                if (!Array.isArray(priceTiers) || priceTiers.length === 0) {
                  return { price: 0, subTotal: 0, usedQty: totalQty };
                }

                let usedQty = Math.max(totalQty, moq);

                if (orderMultiple > 1) {
                  usedQty = Math.ceil(usedQty / orderMultiple) * orderMultiple;
                }

                const sorted = [...priceTiers].sort(
                  (a, b) => a.min_qty - b.min_qty
                );

                let selectedTier = sorted[0];
                for (const tier of sorted) {
                  if (usedQty >= tier.min_qty) {
                    selectedTier = tier;
                  } else {
                    break;
                  }
                }

                const price = parseFloat(selectedTier?.price || 0);
                const subTotal = price * usedQty;

                return { price, subTotal, usedQty };
              };

              let footPrint = "";
              let manufacturerName = "";
              let availabilityFirst = OUT_OF_STOCK;
              let availabilitySecond = OUT_OF_STOCK;
              let availabilityThird = OUT_OF_STOCK;
              let moq = 0;
              let getLcscResponse = await GenerateQuote.getLcsc(items.partName);

              if (
                "data" in getLcscResponse &&
                "result" in getLcscResponse.data &&
                getLcscResponse.data.success === true
              ) {
                getLcscResponse.data.result.product_list.forEach((lcscItem) => {
                  if (items.partName === lcscItem.mpn) {
                    manufacturerName = lcscItem.manufacturer?.name || "";
                    footPrint = lcscItem.package || "";
                    moq = lcscItem.moq || 1;
                    const orderMultiple = lcscItem.order_multiple || 1;
                    const availableQty = lcscItem.quantity || 0;

                    const result1 = getLcscPrice(
                      lcscItem.prices,
                      totalQtyFirst,
                      moq,
                      orderMultiple
                    );
                    const result2 = getLcscPrice(
                      lcscItem.prices,
                      totalQtySecond,
                      moq,
                      orderMultiple
                    );
                    const result3 = getLcscPrice(
                      lcscItem.prices,
                      totalQtyThird,
                      moq,
                      orderMultiple
                    );

                    availabilityFirst =
                      availableQty > totalQtyFirst
                        ? availableQty
                        : OUT_OF_STOCK;
                    availabilitySecond =
                      availableQty > totalQtySecond
                        ? availableQty
                        : OUT_OF_STOCK;
                    availabilityThird =
                      availableQty > totalQtyThird
                        ? availableQty
                        : OUT_OF_STOCK;

                    gqData[items.partName].push({
                      "Part Name": items.partName,
                      "Total Quantity 1": result1.usedQty,
                      "Total Quantity 2": result2.usedQty,
                      "Total Quantity 3": result3.usedQty,
                      manufacturerName: manufacturerName,
                      Footprint: footPrint,
                      Supplier: SUPPLIERS.LCSC,
                      AvailabilityFirst: availabilityFirst,
                      AvailabilitySecond: availabilitySecond,
                      AvailabilityThird: availabilityThird,
                      "Unit Price 1": CURRENCY_SYMBOL + result1.price,
                      "Unit Price 2": CURRENCY_SYMBOL + result2.price,
                      "Unit Price 3": CURRENCY_SYMBOL + result3.price,
                      MOQ: moq,
                      "Subtotal Price 1":
                        CURRENCY_SYMBOL + roundToTwoDecimals(result1.subTotal),
                      "Subtotal Price 2":
                        CURRENCY_SYMBOL + roundToTwoDecimals(result2.subTotal),
                      "Subtotal Price 3":
                        CURRENCY_SYMBOL + roundToTwoDecimals(result3.subTotal),
                    });
                  }
                });
              }
              if (!hasSupplierInData(gqData[items.partName], SUPPLIERS.LCSC)) {
                gqData[items.partName].push(
                  createDefaultSupplierEntry(
                    items.partName,
                    totalQtyFirst,
                    totalQtySecond,
                    totalQtyThird,
                    SUPPLIERS.LCSC
                  )
                );
              }
            }
            return gqData;
          },
          {
            concurrency: 1,
          }
        )
        .then((response) => {
          // eslint-disable-next-line
          response.forEach((newitem) => {
            let splowestFirst = 0;
            let splowestSecond = 0;
            let splowestThird = 0;
            let arrowlowestFirst = 0;
            let arrowlowestSecond = 0;
            let arrowlowestThird = 0;
            let mouserlowestFirst = 0;
            let mouserlowestSecond = 0;
            let mouserlowestThird = 0;
            let tmelowestFirst = 0;
            let tmelowestSecond = 0;
            let tmelowestThird = 0;
            let farnelllowestFirst = 0;
            let farnelllowestSecond = 0;
            let farnelllowestThird = 0;
            let dglowestFirst = 0;
            let dglowestSecond = 0;
            let dglowestThird = 0;
            let lcsclowestFirst = 0;
            let lcsclowestSecond = 0;
            let lcsclowestThird = 0;
            let lowestItemFirst = null;
            let lowestItemSecond = null;
            let lowestItemThird = null;
            const allItems = [];
            // eslint-disable-next-line
            for (const [, value] of Object.entries(newitem)) {
              // eslint-disable-next-line
              value.forEach((newitems) => {
                const priceFirst = extractPrice(newitems["Subtotal Price 1"]);
                const priceSecond = extractPrice(newitems["Subtotal Price 2"]);
                const priceThird = extractPrice(newitems["Subtotal Price 3"]);

                newitems._priceFirst = priceFirst;
                newitems._priceSecond = priceSecond;
                newitems._priceThird = priceThird;

                const newLowestFirst = updateLowestPrice(
                  priceFirst,
                  newitems.AvailabilityFirst,
                  splowestFirst
                );
                const newLowestSecond = updateLowestPrice(
                  priceSecond,
                  newitems.AvailabilitySecond,
                  splowestSecond
                );
                const newLowestThird = updateLowestPrice(
                  priceThird,
                  newitems.AvailabilityThird,
                  splowestThird
                );

                const firstResult = updateLowestItem(
                  newLowestFirst,
                  splowestFirst,
                  priceFirst,
                  newitems,
                  newitems.AvailabilityFirst,
                  lowestItemFirst
                );
                splowestFirst = firstResult.lowest;
                lowestItemFirst = firstResult.item;

                const secondResult = updateLowestItem(
                  newLowestSecond,
                  splowestSecond,
                  priceSecond,
                  newitems,
                  newitems.AvailabilitySecond,
                  lowestItemSecond
                );
                splowestSecond = secondResult.lowest;
                lowestItemSecond = secondResult.item;

                const thirdResult = updateLowestItem(
                  newLowestThird,
                  splowestThird,
                  priceThird,
                  newitems,
                  newitems.AvailabilityThird,
                  lowestItemThird
                );
                splowestThird = thirdResult.lowest;
                lowestItemThird = thirdResult.item;

                const supplier = newitems["Supplier"];
                if (supplier === SUPPLIERS.ARROW) {
                  addToSupplierData(setArrowData, newitems);
                  const arrowLowest = updateSupplierLowestPrices(
                    priceFirst,
                    priceSecond,
                    priceThird,
                    newitems,
                    arrowlowestFirst,
                    arrowlowestSecond,
                    arrowlowestThird
                  );
                  arrowlowestFirst = arrowLowest.first;
                  arrowlowestSecond = arrowLowest.second;
                  arrowlowestThird = arrowLowest.third;
                } else if (supplier === SUPPLIERS.FARNELL) {
                  addToSupplierData(setFarnellData, newitems);
                  const farnellLowest = updateSupplierLowestPrices(
                    priceFirst,
                    priceSecond,
                    priceThird,
                    newitems,
                    farnelllowestFirst,
                    farnelllowestSecond,
                    farnelllowestThird
                  );
                  farnelllowestFirst = farnellLowest.first;
                  farnelllowestSecond = farnellLowest.second;
                  farnelllowestThird = farnellLowest.third;
                } else if (supplier === SUPPLIERS.TME) {
                  addToSupplierData(setTmeData, newitems);
                  const tmeLowest = updateSupplierLowestPrices(
                    priceFirst,
                    priceSecond,
                    priceThird,
                    newitems,
                    tmelowestFirst,
                    tmelowestSecond,
                    tmelowestThird
                  );
                  tmelowestFirst = tmeLowest.first;
                  tmelowestSecond = tmeLowest.second;
                  tmelowestThird = tmeLowest.third;
                } else if (supplier === SUPPLIERS.MOUSER) {
                  addToSupplierData(setMouserData, newitems);
                  const mouserLowest = updateSupplierLowestPrices(
                    priceFirst,
                    priceSecond,
                    priceThird,
                    newitems,
                    mouserlowestFirst,
                    mouserlowestSecond,
                    mouserlowestThird
                  );
                  mouserlowestFirst = mouserLowest.first;
                  mouserlowestSecond = mouserLowest.second;
                  mouserlowestThird = mouserLowest.third;
                } else if (supplier === SUPPLIERS.DIGIKEY) {
                  addToSupplierData(setDigikeyData, newitems);
                  const dgLowest = updateSupplierLowestPrices(
                    priceFirst,
                    priceSecond,
                    priceThird,
                    newitems,
                    dglowestFirst,
                    dglowestSecond,
                    dglowestThird
                  );
                  dglowestFirst = dgLowest.first;
                  dglowestSecond = dgLowest.second;
                  dglowestThird = dgLowest.third;
                } else if (supplier === SUPPLIERS.LCSC) {
                  addToSupplierData(setLcscData, newitems);
                  const lcscLowest = updateSupplierLowestPrices(
                    priceFirst,
                    priceSecond,
                    priceThird,
                    newitems,
                    lcsclowestFirst,
                    lcsclowestSecond,
                    lcsclowestThird
                  );
                  lcsclowestFirst = lcscLowest.first;
                  lcsclowestSecond = lcscLowest.second;
                  lcsclowestThird = lcscLowest.third;
                }

                allItems.push(newitems);
                addToMultipleSupplierData(
                  [setGenerateExcelData, setGenerateQuoteData],
                  newitems
                );
              });
            }

            allItems.forEach((newitems) => {
              const priceFirst = newitems._priceFirst;
              const priceSecond = newitems._priceSecond;
              const priceThird = newitems._priceThird;

              newitems.firstValid =
                lowestItemFirst === newitems &&
                isValidPrice(
                  priceFirst,
                  newitems.AvailabilityFirst,
                  splowestFirst
                );
              newitems.secondValid =
                lowestItemSecond === newitems &&
                isValidPrice(
                  priceSecond,
                  newitems.AvailabilitySecond,
                  splowestSecond
                );
              newitems.thirdValid =
                lowestItemThird === newitems &&
                isValidPrice(
                  priceThird,
                  newitems.AvailabilityThird,
                  splowestThird
                );
            });

            updateSupplierTotals(
              setArrowTotalFirst,
              setArrowTotalSecond,
              setArrowTotalThird,
              arrowlowestFirst,
              arrowlowestSecond,
              arrowlowestThird
            );
            updateSupplierTotals(
              setTmeTotalFirst,
              setTmeTotalSecond,
              setTmeTotalThird,
              tmelowestFirst,
              tmelowestSecond,
              tmelowestThird
            );
            updateSupplierTotals(
              setFarnellTotalFirst,
              setFarnellTotalSecond,
              setFarnellTotalThird,
              farnelllowestFirst,
              farnelllowestSecond,
              farnelllowestThird
            );
            updateSupplierTotals(
              setMouserTotalFirst,
              setMouserTotalSecond,
              setMouserTotalThird,
              mouserlowestFirst,
              mouserlowestSecond,
              mouserlowestThird
            );
            updateSupplierTotals(
              setDigikeyTotalFirst,
              setDigikeyTotalSecond,
              setDigikeyTotalThird,
              dglowestFirst,
              dglowestSecond,
              dglowestThird
            );
            updateSupplierTotals(
              setLcscTotalFirst,
              setLcscTotalSecond,
              setLcscTotalThird,
              lcsclowestFirst,
              lcsclowestSecond,
              lcsclowestThird
            );
          });

          setLoading(false);
        })
        .catch((err) => {
          handleQuoteError(err, setLoading);
        });
    } catch (err) {
      handleQuoteError(err, setLoading);
    }
  };
  return (
    <div>
      {loading && (
        <div className="loader-container">
          <div className="spinner"></div>
          <h3 className="spinnerMessage">{loadingMessage}</h3>
        </div>
      )}
      <div className="container">
        <div className="main-content">
          <header className="jumbotron">
            <h3 className="row justify-content-md-center">
              Electronic Component Quoter
            </h3>
          </header>

          <div style={{ maxHeight: "250px", overflow: "auto" }}>
            <table className="table">
              <thead>
                <tr key="pastData">
                  <th key="partName">Part Number</th>
                  <th key="unitsPerPCB">Units Per PCB</th>
                </tr>
              </thead>
              <tbody>
                {pastedData.length > 0 &&
                  pastedData.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      <td key={rowIndex + row.partName} className={row.color}>
                        {row.partName}
                      </td>
                      <td
                        key={rowIndex + row.unitsPerPCB}
                        className={row.color}
                      >
                        {row.unitsPerPCB}
                      </td>
                    </tr>
                  ))}
                {pastedData.length === 0 && (
                  <tr>
                    <td className={style} onClick={changeStyle}>
                      {pastedMessage}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <hr />
          <div className="form-outline">
            <label className="form-label" htmlFor="pcbquantityfirst">
              PCBs Quantity 1
            </label>
            <input
              onChange={(e) => handleQuantityChange(e, setPcbquantityFirst)}
              type="text"
              id="pcbquantityfirst"
              name="pcbquantityfirst"
              className="form-control"
              min="1"
              value={pcbquantityFirst}
            />
            <label className="form-label" htmlFor="pcbquantitysecond">
              PCBs Quantity 2
            </label>
            <input
              onChange={(e) => handleQuantityChange(e, setPcbquantitySecond)}
              type="text"
              id="pcbquantitysecond"
              name="pcbquantitysecond"
              className="form-control"
              min="1"
              value={pcbquantitySecond}
            />
            <label className="form-label" htmlFor="pcbquantitythird">
              PCBs Quantity 3
            </label>
            <input
              onChange={(e) => handleQuantityChange(e, setPcbquantityThird)}
              type="text"
              id="pcbquantitythird"
              name="pcbquantitythird"
              className="form-control"
              min="1"
              value={pcbquantityThird}
            />
            <hr />
            <span className="form-label">Supplier</span>
            <div className="form-check">
              <input
                onChange={() => setArrow(!arrow)}
                className="form-check-input"
                type="checkbox"
                checked={arrow}
                id="arrow"
                name="arrow"
              />
              <label className="form-check-label" htmlFor="arrow">
                Arrow
              </label>
            </div>
            <div className="form-check">
              <input
                onChange={() => setTme(!tme)}
                className="form-check-input"
                type="checkbox"
                checked={tme}
                id="tme"
                name="tme"
              />
              <label className="form-check-label" htmlFor="tme">
                TME
              </label>
            </div>
            <div className="form-check">
              <input
                onChange={() => setFarnell(!farnell)}
                className="form-check-input"
                type="checkbox"
                checked={farnell}
                id="farnell"
                name="farnell"
              />
              <label className="form-check-label" htmlFor="farnell">
                Farnell
              </label>
            </div>
            <div className="form-check">
              <input
                onChange={() => setDigikey(!digikey)}
                className="form-check-input"
                type="checkbox"
                checked={digikey}
                id="digikey"
                name="digikey"
              />
              <label className="form-check-label" htmlFor="digikey">
                DigiKey
              </label>
            </div>
            <div className="form-check">
              <input
                onChange={() => setMouser(!mouser)}
                className="form-check-input"
                type="checkbox"
                checked={mouser}
                id="mouser"
                name="mouser"
              />
              <label className="form-check-label" htmlFor="mouser">
                Mouser
              </label>
            </div>
            <div className="form-check">
              <input
                onChange={() => setLcsc(!lcsc)}
                className="form-check-input"
                type="checkbox"
                checked={lcsc}
                id="lcsc"
                name="lcsc"
              />
              <label className="form-check-label" htmlFor="lcsc">
                LCSC
              </label>
            </div>
          </div>
          <div className="form-group mt-4">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm me-1"
              onClick={handleGenerateQuote}
            >
              Generate Quotation
            </button>

            <button
              type="button"
              className="btn btn-outline-warning btn-sm me-1"
              onClick={handleReset}
            >
              Reset
            </button>

            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleLcsc}
            >
              Lcsc
            </button>
          </div>
          <hr />
          {arrowData.length > 0 ||
          tmeData.length > 0 ||
          mouserData.length > 0 ||
          digikeyData.length > 0 ||
          farnellData.length > 0 ||
          lcscData.length > 0 ? (
            <div>
              <ExportToExcel
                excelData={generateExcelData}
                excelTotalData={excelTotalData}
                fileName={fileName}
              />
              <hr />
              <div style={{ margin: "12px" }}>
                <div className="row border">
                  <div className="col border"></div>
                  <div className="col border">PCBs Quantity 1</div>
                  <div className="col border">PCBs Quantity 2</div>
                  <div className="col border">PCBs Quantity 3</div>
                </div>
                {tmeData.length > 0 && (
                  <div className="row border">
                    <div className="col border">TME Total</div>
                    <div className="col border">
                      {CURRENCY_SYMBOL}
                      {tmeTotalFirst}
                    </div>
                    <div className="col border">
                      {CURRENCY_SYMBOL}
                      {tmeTotalSecond}
                    </div>
                    <div className="col border">
                      {CURRENCY_SYMBOL}
                      {tmeTotalThird}
                    </div>
                  </div>
                )}
                {arrowData.length > 0 && (
                  <div className="row border">
                    <div className="col border">Arrow Total</div>
                    <div className="col border">
                      {CURRENCY_SYMBOL}
                      {arrowTotalFirst}
                    </div>
                    <div className="col border">
                      {CURRENCY_SYMBOL}
                      {arrowTotalSecond}
                    </div>
                    <div className="col border">
                      {CURRENCY_SYMBOL}
                      {arrowTotalThird}
                    </div>
                  </div>
                )}
                {mouserData.length > 0 && (
                  <div className="row border">
                    <div className="col border">Mouser Total</div>
                    <div className="col border">
                      {CURRENCY_SYMBOL}
                      {mouserTotalFirst}
                    </div>
                    <div className="col border">
                      {CURRENCY_SYMBOL}
                      {mouserTotalSecond}
                    </div>
                    <div className="col border">
                      {CURRENCY_SYMBOL}
                      {mouserTotalThird}
                    </div>
                  </div>
                )}
                {farnellData.length > 0 && (
                  <div className="row border">
                    <div className="col border">Farnell Total</div>
                    <div className="col border">
                      {CURRENCY_SYMBOL}
                      {farnellTotalFirst}
                    </div>
                    <div className="col border">
                      {CURRENCY_SYMBOL}
                      {farnellTotalSecond}
                    </div>
                    <div className="col border">
                      {CURRENCY_SYMBOL}
                      {farnellTotalThird}
                    </div>
                  </div>
                )}
                {digikeyData.length > 0 && (
                  <div className="row border">
                    <div className="col border">DigiKey Total</div>
                    <div className="col border">
                      {CURRENCY_SYMBOL}
                      {digikeyTotalFirst}
                    </div>
                    <div className="col border">
                      {CURRENCY_SYMBOL}
                      {digikeyTotalSecond}
                    </div>
                    <div className="col border">
                      {CURRENCY_SYMBOL}
                      {digikeyTotalThird}
                    </div>
                  </div>
                )}
                {lcscData.length > 0 && (
                  <div className="row border">
                    <div className="col border">Lcsc Total</div>
                    <div className="col border">
                      {CURRENCY_SYMBOL}
                      {lcscTotalFirst}
                    </div>
                    <div className="col border">
                      {CURRENCY_SYMBOL}
                      {lcscTotalSecond}
                    </div>
                    <div className="col border">
                      {CURRENCY_SYMBOL}
                      {lcscTotalThird}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ maxHeight: "300px", overflow: "auto" }}>
                <table className="table bordered">
                  <thead>
                    <tr key="generate Quote Data">
                      <th key="Part Name">Part Name</th>
                      <th key="Footprint">Footprint</th>
                      <th key="Supplier">Supplier</th>
                      <th key="Manufacturer Name">Manufacturer Name</th>
                      <th key="MOQ">MOQ</th>
                      <th key="Availability 1">Availability 1</th>
                      <th key="Total Quantity 1">Total Quantity 1</th>
                      <th key="Unit Price 1">Unit Price 1</th>
                      <th key="Subtotal Price 1">Subtotal Price 1</th>
                      <th key="Availability 2">Availability 2</th>
                      <th key="Total Quantity 2">Total Quantity 2</th>
                      <th key="Unit Price 2">Unit Price 2</th>
                      <th key="Subtotal Price 2">Subtotal Price 2</th>
                      <th key="Availability 3">Availability 3</th>
                      <th key="Total Quantity 3">Total Quantity 3</th>
                      <th key="Unit Price 3">Unit Price 3</th>
                      <th key="Subtotal Price 3">Subtotal Price 3</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generateQuoteData.length > 0 &&
                      generateQuoteData.map((row, rowIndex) => (
                        <tr key={rowIndex + row + 1}>
                          <td key={row + row["Part Name"] + 2}>
                            {row["Part Name"]}
                          </td>
                          <td key={row + row["Footprint"] + 3}>
                            {row["Footprint"]}
                          </td>
                          <td key={row + row["Supplier"] + 4}>
                            {row["Supplier"]}
                          </td>
                          <td key={row + row["manufacturerName"] + 5}>
                            {row["manufacturerName"]}
                          </td>
                          <td key={row + row["MOQ"] + 6}>{row["MOQ"]}</td>
                          <td key={row + row["AvailabilityFirst"] + 7}>
                            {row["AvailabilityFirst"]}
                          </td>
                          <td key={row + row["Total Quantity 1"] + 8}>
                            {row["Total Quantity 1"]}
                          </td>
                          <td key={row + row["Unit Price 1"] + 9}>
                            {row["Unit Price 1"]}
                          </td>
                          <td
                            className={row.firstValid ? "valid" : ""}
                            key={row + row["Subtotal Price 1"] + 10}
                          >
                            {row["Subtotal Price 1"]}
                          </td>
                          <td key={row + row["AvailabilitySecond"] + 11}>
                            {row["AvailabilitySecond"]}
                          </td>
                          <td key={row + row["Total Quantity 2"] + 12}>
                            {row["Total Quantity 2"]}
                          </td>
                          <td key={row + row["Unit Price 2"] + 13}>
                            {row["Unit Price 2"]}
                          </td>
                          <td
                            className={row.secondValid ? "valid" : ""}
                            key={row + row["Subtotal Price 2"] + 14}
                          >
                            {row["Subtotal Price 2"]}
                          </td>
                          <td key={row + row["AvailabilityThird"] + 15}>
                            {row["AvailabilityThird"]}
                          </td>
                          <td key={row + row["Total Quantity 3"] + 16}>
                            {row["Total Quantity 3"]}
                          </td>
                          <td key={row + row["Unit Price 3"] + 17}>
                            {row["Unit Price 3"]}
                          </td>
                          <td
                            className={row.thirdValid ? "valid" : ""}
                            key={row + row["Subtotal Price 3"] + 18}
                          >
                            {row["Subtotal Price 3"]}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <hr />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
export default UserDashboard;
